import { useEffect } from 'react';
import { formatRoute, type LandingId } from '@visious/core';
import { navigate, useRoute } from './router';
import { readSettings } from '../platform/settings';

/**
 * §8's `default_home` — the landing page, now that `/` is nobody's destination.
 *
 * The nav's first item is Popular now and there is no Home entry, so `/` has exactly one meaning
 * left: *the place opening Visious takes you*. Every arrival — the app being opened, the brand link,
 * a bookmark — is therefore redirected to the landing destination, not only the first one. (The
 * previous version redirected on mount alone, which was right while Home was a real page a nav item
 * pointed at.)
 *
 * It is a `replaceState`, `useCanonicalUrl`'s reasoning: nobody asked to visit `/`, so leaving the
 * landing page must not cost a Back press through it.
 */
export function useLandingRedirect(): void {
  const route = useRoute();
  const landing = route.kind === 'home' ? readSettings().landing : null;

  useEffect(() => {
    if (landing === null) return;
    navigate(formatRoute(routeFor(landing)), { replace: true });
  }, [landing]);
}

/** Every landing destination is a `/feed/*` page — Popular included, since it got its own URL. */
function routeFor(landing: LandingId): Parameters<typeof formatRoute>[0] {
  return { kind: 'feed', feed: landing };
}
